/**
 * LogisticsBackground — Container shipping industry vector illustration.
 *
 * Focused on container shipping, port operations, and transport:
 * container vessel with stacked containers, STS gantry crane, RTG yard crane,
 * container truck on chassis, container yard stacks, reach stacker,
 * sea lanes, and port terminal.
 *
 * Props:
 *  - tint: a single hex color (e.g. '#3b82f6'). All elements render in shades
 *          of this color via opacity. When omitted, uses currentColor.
 */

import type { AccentColor } from '@/stores/app-store';

/** Map accent name → hex for tint mode */
export const ACCENT_HEX: Record<AccentColor, string> = {
  blue:   '#3b82f6',
  rose:   '#f43f5e',
  pink:   '#ec4899',
  purple: '#a855f7',
  teal:   '#14b8a6',
  green:  '#22c55e',
  amber:  '#f59e0b',
  indigo: '#6366f1',
  orange: '#f97316',
  slate:  '#64748b',
};

interface LogisticsBackgroundProps {
  className?: string;
  /** Single tint color — all elements use this color at varying opacities */
  tint?: string;
  fit?: 'cover' | 'contain';
}

export function LogisticsBackground({ className = '', tint, fit = 'cover' }: LogisticsBackgroundProps) {
  const base = tint ?? 'currentColor';
  const c = {
    sea: base, seaLight: base, route: base,
    hull: base, deck: base, funnel: base,
    ctn1: base, ctn2: base, ctn3: base, ctn4: base,
    ctn5: base, ctn6: base, ctn7: base,
    crane: base, craneSteel: base,
    truck: base, truckCab: base, chassis: base,
    ground: base, terminal: base, machine: base,
  };
  const op = tint ? 0.35 : 1;

  return (
    <svg
      className={className}
      viewBox="0 0 800 600"
      preserveAspectRatio={fit === 'contain' ? 'xMidYMid meet' : 'xMidYMid slice'}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      {...(tint ? { style: { opacity: 1 } } : {})}
    >
      {/* ── Sea / shipping lanes ── */}
      <path d="M0 480 Q200 440 400 460 Q600 480 800 450" stroke={c.seaLight} strokeWidth="1.5" strokeDasharray="8 6" opacity={tint ? op * 0.5 : 0.3} />
      <path d="M0 500 Q150 470 350 490 Q550 510 800 475" stroke={c.seaLight} strokeWidth="1" strokeDasharray="5 8" opacity={tint ? op * 0.4 : 0.2} />
      <path d="M0 520 Q250 500 500 515 Q700 530 800 505" stroke={c.seaLight} strokeWidth="0.8" strokeDasharray="4 10" opacity={tint ? op * 0.3 : 0.15} />

      {/* ── Container Vessel (center-bottom) — large with stacked containers ── */}
      <g transform="translate(180, 390)" opacity={tint ? op : undefined}>
        {/* Hull */}
        <path d="M0 55 L15 80 L235 80 L250 55 Z" fill={c.hull} />
        <path d="M10 55 L240 55 L240 45 L10 45 Z" fill={c.deck} opacity="0.8" />
        {/* Container rows — 3 tiers stacked */}
        {/* Row 1 (bottom) */}
        <rect x="18" y="25" width="26" height="20" rx="1" fill={c.ctn1} opacity="0.85" />
        <rect x="46" y="25" width="26" height="20" rx="1" fill={c.ctn2} opacity="0.85" />
        <rect x="74" y="25" width="26" height="20" rx="1" fill={c.ctn3} opacity="0.85" />
        <rect x="102" y="25" width="26" height="20" rx="1" fill={c.ctn4} opacity="0.85" />
        <rect x="130" y="25" width="26" height="20" rx="1" fill={c.ctn7} opacity="0.85" />
        <rect x="158" y="25" width="26" height="20" rx="1" fill={c.ctn6} opacity="0.85" />
        <rect x="186" y="25" width="26" height="20" rx="1" fill={c.ctn1} opacity="0.85" />
        {/* Row 2 (middle) */}
        <rect x="32" y="5" width="26" height="20" rx="1" fill={c.ctn5} opacity="0.8" />
        <rect x="60" y="5" width="26" height="20" rx="1" fill={c.ctn1} opacity="0.8" />
        <rect x="88" y="5" width="26" height="20" rx="1" fill={c.ctn7} opacity="0.8" />
        <rect x="116" y="5" width="26" height="20" rx="1" fill={c.ctn3} opacity="0.8" />
        <rect x="144" y="5" width="26" height="20" rx="1" fill={c.ctn2} opacity="0.8" />
        <rect x="172" y="5" width="26" height="20" rx="1" fill={c.ctn4} opacity="0.8" />
        {/* Row 3 (top) */}
        <rect x="46" y="-13" width="26" height="18" rx="1" fill={c.ctn4} opacity="0.75" />
        <rect x="74" y="-13" width="26" height="18" rx="1" fill={c.ctn6} opacity="0.75" />
        <rect x="102" y="-13" width="26" height="18" rx="1" fill={c.ctn2} opacity="0.75" />
        <rect x="130" y="-13" width="26" height="18" rx="1" fill={c.ctn5} opacity="0.75" />
        <rect x="158" y="-13" width="26" height="18" rx="1" fill={c.ctn1} opacity="0.75" />
        {/* Bridge / funnel */}
        <rect x="210" y="8" width="18" height="37" rx="2" fill={c.deck} opacity="0.9" />
        <rect x="213" y="14" width="12" height="8" rx="1" fill={c.hull} opacity="0.3" />
        <rect x="215" y="-4" width="8" height="12" rx="1" fill={c.funnel} opacity="0.8" />
        {/* Waves */}
        <path d="M-20 88 Q30 78 80 88 Q130 98 180 88 Q230 78 280 88" stroke={c.seaLight} strokeWidth="1.5" opacity="0.5" />
        <path d="M-10 94 Q40 84 90 94 Q140 104 190 94 Q240 84 290 94" stroke={c.seaLight} strokeWidth="1" opacity="0.25" />
      </g>

      {/* ── STS Gantry Crane (top-left) — ship-to-shore ── */}
      <g transform="translate(50, 100)" opacity={tint ? op : undefined}>
        {/* Legs */}
        <rect x="0" y="80" width="8" height="70" fill={c.crane} />
        <rect x="72" y="80" width="8" height="70" fill={c.crane} />
        {/* Rail base */}
        <rect x="-4" y="148" width="88" height="6" rx="1" fill={c.craneSteel} />
        {/* Horizontal boom */}
        <rect x="-30" y="76" width="140" height="8" rx="1" fill={c.crane} />
        {/* Trolley rail */}
        <rect x="-25" y="73" width="130" height="3" fill={c.craneSteel} opacity="0.6" />
        {/* Spreader + container being lifted */}
        <line x1="40" y1="84" x2="40" y2="110" stroke={c.craneSteel} strokeWidth="1.5" />
        <rect x="28" y="110" width="24" height="4" rx="0.5" fill={c.craneSteel} />
        <rect x="30" y="114" width="20" height="14" rx="1" fill={c.ctn3} opacity="0.8" />
        {/* Top structure */}
        <path d="M4 76 L36 50 L44 50 L76 76" stroke={c.crane} strokeWidth="3" />
        <rect x="32" y="44" width="16" height="8" rx="1" fill={c.crane} opacity="0.8" />
      </g>

      {/* ── Container Yard Stack (top-center) — 4×3 grid ── */}
      <g transform="translate(280, 80)" opacity={tint ? op * 0.9 : 0.9}>
        {/* Ground marking */}
        <rect x="-5" y="62" width="135" height="3" rx="0.5" fill={c.ground} opacity="0.3" />
        {/* Stack row 1 */}
        <rect x="0"  y="42" width="28" height="18" rx="1" fill={c.ctn1} opacity="0.8" />
        <rect x="30" y="42" width="28" height="18" rx="1" fill={c.ctn7} opacity="0.8" />
        <rect x="60" y="42" width="28" height="18" rx="1" fill={c.ctn3} opacity="0.8" />
        <rect x="90" y="42" width="28" height="18" rx="1" fill={c.ctn4} opacity="0.8" />
        {/* Stack row 2 */}
        <rect x="0"  y="22" width="28" height="18" rx="1" fill={c.ctn2} opacity="0.75" />
        <rect x="30" y="22" width="28" height="18" rx="1" fill={c.ctn5} opacity="0.75" />
        <rect x="60" y="22" width="28" height="18" rx="1" fill={c.ctn6} opacity="0.75" />
        <rect x="90" y="22" width="28" height="18" rx="1" fill={c.ctn1} opacity="0.75" />
        {/* Stack row 3 */}
        <rect x="0"  y="2" width="28" height="18" rx="1" fill={c.ctn4} opacity="0.7" />
        <rect x="30" y="2" width="28" height="18" rx="1" fill={c.ctn3} opacity="0.7" />
        <rect x="60" y="2" width="28" height="18" rx="1" fill={c.ctn7} opacity="0.7" />
        <rect x="90" y="2" width="28" height="18" rx="1" fill={c.ctn2} opacity="0.7" />
      </g>

      {/* ── RTG Yard Crane (top-right) — rubber-tired gantry ── */}
      <g transform="translate(560, 60)" opacity={tint ? op * 0.85 : 0.8}>
        {/* Legs */}
        <rect x="0" y="50" width="5" height="55" fill={c.crane} />
        <rect x="65" y="50" width="5" height="55" fill={c.crane} />
        {/* Cross beam */}
        <rect x="-3" y="46" width="76" height="6" rx="1" fill={c.crane} />
        {/* Trolley */}
        <rect x="22" y="43" width="26" height="4" rx="0.5" fill={c.craneSteel} />
        {/* Hoist cable + spreader */}
        <line x1="35" y1="53" x2="35" y2="75" stroke={c.craneSteel} strokeWidth="1" />
        <rect x="25" y="75" width="20" height="3" rx="0.5" fill={c.craneSteel} />
        <rect x="26" y="78" width="18" height="12" rx="1" fill={c.ctn5} opacity="0.8" />
        {/* Wheels */}
        <circle cx="2.5" cy="108" r="3" fill={c.craneSteel} />
        <circle cx="67.5" cy="108" r="3" fill={c.craneSteel} />
        {/* Ground containers below */}
        <rect x="8" y="90" width="22" height="14" rx="1" fill={c.ctn6} opacity="0.6" />
        <rect x="32" y="90" width="22" height="14" rx="1" fill={c.ctn2} opacity="0.6" />
      </g>

      {/* ── Container Truck + Chassis (right side) ── */}
      <g transform="translate(540, 300)" opacity={tint ? op : undefined}>
        {/* Chassis frame */}
        <rect x="0" y="22" width="120" height="6" rx="1" fill={c.chassis} />
        {/* 40ft container on chassis */}
        <rect x="5" y="0" width="100" height="22" rx="2" fill={c.ctn1} opacity="0.85" />
        {/* Container door lines */}
        <line x1="55" y1="2" x2="55" y2="20" stroke={c.ctn1} strokeWidth="0.8" opacity="0.3" />
        <rect x="92" y="4" width="10" height="14" rx="1" fill={c.ctn1} opacity="0.2" />
        {/* Cab */}
        <rect x="120" y="8" width="24" height="20" rx="3" fill={c.truckCab} />
        <rect x="126" y="12" width="12" height="8" rx="1.5" fill={c.truck} opacity="0.3" />
        {/* Wheels */}
        <circle cx="20" cy="32" r="5" fill={c.chassis} />
        <circle cx="45" cy="32" r="5" fill={c.chassis} />
        <circle cx="95" cy="32" r="5" fill={c.chassis} />
        <circle cx="132" cy="32" r="5" fill={c.chassis} />
        <circle cx="20" cy="32" r="2" fill={c.truck} opacity="0.2" />
        <circle cx="45" cy="32" r="2" fill={c.truck} opacity="0.2" />
        <circle cx="95" cy="32" r="2" fill={c.truck} opacity="0.2" />
        <circle cx="132" cy="32" r="2" fill={c.truck} opacity="0.2" />
      </g>

      {/* ── Second truck (smaller, mid-left) ── */}
      <g transform="translate(30, 290)" opacity={tint ? op * 0.7 : 0.6}>
        <rect x="0" y="18" width="90" height="5" rx="1" fill={c.chassis} />
        <rect x="4" y="0" width="72" height="18" rx="1.5" fill={c.ctn7} opacity="0.8" />
        <rect x="90" y="6" width="18" height="17" rx="2" fill={c.truckCab} />
        <circle cx="16" cy="26" r="4" fill={c.chassis} />
        <circle cx="70" cy="26" r="4" fill={c.chassis} />
        <circle cx="100" cy="26" r="4" fill={c.chassis} />
      </g>

      {/* ── Reach Stacker (mid area) ── */}
      <g transform="translate(440, 200)" opacity={tint ? op * 0.8 : 0.7}>
        {/* Body */}
        <rect x="0" y="12" width="28" height="28" rx="3" fill={c.machine} />
        {/* Cab */}
        <rect x="4" y="4" width="14" height="12" rx="2" fill={c.machine} opacity="0.8" />
        <rect x="6" y="6" width="8" height="6" rx="1" fill={c.machine} opacity="0.3" />
        {/* Boom arm */}
        <line x1="28" y1="14" x2="55" y2="4" stroke={c.machine} strokeWidth="3" />
        <line x1="55" y1="4" x2="55" y2="20" stroke={c.machine} strokeWidth="2" />
        {/* Container being carried */}
        <rect x="45" y="20" width="22" height="14" rx="1" fill={c.ctn4} opacity="0.75" />
        {/* Wheels */}
        <circle cx="8" cy="44" r="6" fill={c.machine} />
        <circle cx="22" cy="44" r="6" fill={c.machine} />
        <circle cx="8" cy="44" r="2.5" fill={c.machine} opacity="0.2" />
        <circle cx="22" cy="44" r="2.5" fill={c.machine} opacity="0.2" />
      </g>

      {/* ── Scattered containers (yard) ── */}
      <rect x="170" y="200" width="24" height="14" rx="1.5" fill={c.ctn2} opacity={tint ? op * 0.6 : 0.4} />
      <rect x="198" y="196" width="24" height="14" rx="1.5" fill={c.ctn6} opacity={tint ? op * 0.5 : 0.35} />
      <rect x="172" y="184" width="24" height="14" rx="1.5" fill={c.ctn3} opacity={tint ? op * 0.45 : 0.3} />

      <rect x="680" y="200" width="22" height="13" rx="1" fill={c.ctn3} opacity={tint ? op * 0.5 : 0.35} />
      <rect x="705" y="196" width="22" height="13" rx="1" fill={c.ctn1} opacity={tint ? op * 0.45 : 0.3} />
      <rect x="682" y="185" width="22" height="13" rx="1" fill={c.ctn5} opacity={tint ? op * 0.4 : 0.25} />

      {/* ── Small container stacks (bottom-left) ── */}
      <g transform="translate(30, 520)" opacity={tint ? op * 0.6 : 0.4}>
        <rect x="0" y="0" width="22" height="14" rx="1" fill={c.ctn4} />
        <rect x="24" y="0" width="22" height="14" rx="1" fill={c.ctn2} />
        <rect x="48" y="0" width="22" height="14" rx="1" fill={c.ctn1} />
        <rect x="6" y="-14" width="22" height="13" rx="1" fill={c.ctn7} />
        <rect x="30" y="-14" width="22" height="13" rx="1" fill={c.ctn3} />
      </g>

      {/* ── Port terminal building (far right) ── */}
      <g transform="translate(690, 360)" opacity={tint ? op * 0.6 : 0.5}>
        <rect x="0" y="10" width="60" height="40" rx="2" fill={c.terminal} />
        <rect x="5" y="0" width="50" height="12" rx="1.5" fill={c.terminal} opacity="0.7" />
        <rect x="8" y="22" width="12" height="14" rx="1" fill={c.terminal} opacity="0.2" />
        <rect x="24" y="22" width="12" height="14" rx="1" fill={c.terminal} opacity="0.2" />
        <rect x="40" y="22" width="12" height="14" rx="1" fill={c.terminal} opacity="0.2" />
        {/* Quay wall */}
        <rect x="-10" y="50" width="80" height="4" rx="0.5" fill={c.ground} opacity="0.4" />
      </g>

      {/* ── Container ID markings (decorative text-like bars) ── */}
      <g transform="translate(300, 340)" opacity={tint ? op * 0.4 : 0.25}>
        <rect x="0" y="0" width="40" height="2.5" rx="0.5" fill={c.ground} />
        <rect x="0" y="5" width="28" height="2" rx="0.5" fill={c.ground} />
        <rect x="0" y="9" width="35" height="2" rx="0.5" fill={c.ground} />
      </g>
      <g transform="translate(100, 400)" opacity={tint ? op * 0.35 : 0.2}>
        <rect x="0" y="0" width="36" height="2.5" rx="0.5" fill={c.ground} />
        <rect x="0" y="5" width="24" height="2" rx="0.5" fill={c.ground} />
      </g>

      {/* ── Dotted shipping route arcs ── */}
      <path
        d="M80 560 Q200 500 380 520 Q550 540 720 480"
        stroke={c.route}
        strokeWidth="1.5"
        strokeDasharray="6 5"
        opacity={tint ? op * 0.5 : 0.25}
      />
      <path
        d="M50 340 Q150 280 300 310 Q450 340 580 280"
        stroke={c.route}
        strokeWidth="1"
        strokeDasharray="4 6"
        opacity={tint ? op * 0.4 : 0.2}
      />
    </svg>
  );
}
